import React, { useEffect, useState } from 'react';
import { apiClient } from '../../lib/apiClient';

export interface ImageEditRequest {
  sourceUrl: string;
  prompt: string;
  operation?: string;
  size?: string;
}

interface ImageViewerProps {
  url: string;
  onClose: () => void;
  onRequestEdit: (edit: ImageEditRequest) => void;
  onRemoved: () => void;
}

const REFRAME_OPTIONS: { size: string; label: string }[] = [
  { size: '1024x1024', label: 'Square' },
  { size: '1024x1536', label: 'Portrait' },
  { size: '1536x1024', label: 'Landscape' },
];

// Full-screen viewer for a generated image, ported from public/index.html's
// openImageViewer -- that implementation already has a real, working
// click-to-expand + edit/reframe/redesign/background-remove/delete flow
// wired to POST /api/image/edit and POST /api/images/delete (via
// lib/chatservice.js's processImage, ownership-checked). apps/web had the
// image display and a working download button but nothing else; this reuses
// the same backend contract rather than inventing a new one.
export function ImageViewer({ url, onClose, onRequestEdit, onRemoved }: ImageViewerProps) {
  const [reframeOpen, setReframeOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const fileName = url.split('/').pop()?.split('?')[0] || 'ozira-image.png';

  const requestEdit = (prompt: string, options: { operation?: string; size?: string } = {}) => {
    onRequestEdit({ sourceUrl: url, prompt, ...options });
    onClose();
  };

  const handleRemove = async () => {
    if (removing) return;
    if (!window.confirm('Remove this image from your account? This cannot be undone.')) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await apiClient.call('/api/images/delete', { body: { url } });
      onRemoved();
      onClose();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Could not remove this image.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div
      className="ozira-cw-image-viewer-backdrop"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ozira-cw-image-viewer" role="dialog" aria-modal="true" aria-label="Image viewer">
        <div className="ozira-cw-image-viewer__top">
          <button type="button" className="ozira-cw-image-viewer__close" aria-label="Close" onClick={onClose}>×</button>
          <div className="ozira-cw-image-viewer__title">Generated image</div>
          <a
            className="ozira-cw-image-viewer__save"
            href={url}
            download={fileName}
          >
            Save
          </a>
        </div>
        <div className="ozira-cw-image-viewer__body">
          <img src={url} alt="Generated by OZIRA" />
          {reframeOpen ? (
            <div className="ozira-cw-image-viewer__reframe" role="group" aria-label="Reframe image">
              {REFRAME_OPTIONS.map((option) => (
                <button
                  key={option.size}
                  type="button"
                  onClick={() => requestEdit('Resize and recompose this image without changing its main subject.', { size: option.size })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="ozira-cw-image-viewer__tools" role="group" aria-label="Image tools">
          <button type="button" onClick={() => requestEdit('Edit this image: ')}>Edit</button>
          <button type="button" onClick={() => requestEdit('')}>Redesign</button>
          <button type="button" onClick={() => setReframeOpen((open) => !open)} aria-pressed={reframeOpen}>Reframe</button>
          <button
            type="button"
            onClick={() => requestEdit(
              'Remove the existing background, isolate the main subject cleanly, and place it on a plain white background. Preserve the subject exactly.',
              { operation: 'remove_background' },
            )}
          >
            Remove background
          </button>
          <button type="button" className="ozira-cw-image-viewer__remove" onClick={handleRemove} disabled={removing}>
            {removing ? 'Removing…' : 'Delete'}
          </button>
        </div>
        {removeError ? <div className="ozira-cw-image-viewer__error" role="alert">{removeError}</div> : null}
      </div>
    </div>
  );
}
